import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Check, Users, Mail, User, Instagram, Loader2 } from 'lucide-react';
import api from '../services/api';

export default function CommunityJoin() {
  const { slug } = useParams();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    instagram_username: '',
    email: '',
    name: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        const response = await api.get(`/community/${slug}`);
        setPageData(response.data);
      } catch (err) {
        console.error('Error fetching community page:', err);
        if (err.response?.status === 404) {
          setError('This community page does not exist.');
        } else {
          setError('Failed to load community page. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchPage();
    }
  }, [slug]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await api.post(`/community/${slug}/join`, formData);
      setSubmitted(true);
    } catch (err) {
      console.error('Error joining community:', err);
      // Don't show error for duplicate email (privacy)
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
            <Users className="w-8 h-8 text-gray-500" />
          </div>
          <h1 className="text-xl font-semibold text-white mb-2">Page Not Found</h1>
          <p className="text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div 
            className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
            style={{ backgroundColor: pageData?.primary_color || '#6366f1' }}
          >
            <Check className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-4">You're In!</h1>
          <p className="text-gray-300 text-lg mb-8">
            Welcome to the community! Check your inbox for next steps.
          </p>
          {pageData?.instagram_username && (
            <a
              href={`https://instagram.com/${pageData.instagram_username}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-indigo-400 hover:text-indigo-300 transition-colors"
            >
              <Instagram className="w-5 h-5" />
              Follow @{pageData.instagram_username}
            </a>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Profile Section */}
        <div className="text-center mb-8">
          {pageData?.profile_picture_url ? (
            <img
              src={pageData.profile_picture_url}
              alt={pageData.instagram_username}
              className="w-24 h-24 rounded-full mx-auto mb-4 border-4"
              style={{ borderColor: pageData.primary_color || '#6366f1' }}
            />
          ) : (
            <div 
              className="w-24 h-24 rounded-full mx-auto mb-4 flex items-center justify-center"
              style={{ backgroundColor: pageData?.primary_color || '#6366f1' }}
            >
              <User className="w-12 h-12 text-white" />
            </div>
          )}
          {pageData?.instagram_username && (
            <a
              href={`https://instagram.com/${pageData.instagram_username}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-indigo-400 hover:text-indigo-300 text-sm mb-2 inline-flex items-center gap-1"
            >
              <Instagram className="w-4 h-4" />
              @{pageData.instagram_username}
            </a>
          )}
        </div>

        {/* Form Card */}
        <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700">
          <h1 className="text-2xl font-bold text-white text-center mb-2">
            {pageData?.headline || 'Join my community!'}
          </h1>
          {pageData?.description && (
            <p className="text-gray-400 text-center mb-6">
              {pageData.description}
            </p>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Instagram Username */}
            <div>
              <label htmlFor="instagram_username" className="block text-sm font-medium text-gray-300 mb-1">
                Instagram Username
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Instagram className="h-5 w-5 text-gray-500" />
                </div>
                <input
                  type="text"
                  id="instagram_username"
                  name="instagram_username"
                  value={formData.instagram_username}
                  onChange={handleChange}
                  placeholder="your_username"
                  required
                  className="block w-full pl-10 pr-3 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                  style={{ '--tw-ring-color': pageData?.primary_color || '#6366f1' }}
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">
                Email Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-500" />
                </div>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  required
                  className="block w-full pl-10 pr-3 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                  style={{ '--tw-ring-color': pageData?.primary_color || '#6366f1' }}
                />
              </div>
            </div>

            {/* Name (Optional) */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-1">
                Your Name <span className="text-gray-500">(optional)</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gray-500" />
                </div>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="John Doe"
                  className="block w-full pl-10 pr-3 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                  style={{ '--tw-ring-color': pageData?.primary_color || '#6366f1' }}
                />
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3 px-4 rounded-lg font-semibold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              style={{ 
                backgroundColor: pageData?.primary_color || '#6366f1',
                '--tw-ring-color': pageData?.primary_color || '#6366f1'
              }}
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Joining...
                </>
              ) : (
                <>
                  <Users className="w-5 h-5" />
                  {pageData?.button_text || 'Join'}
                </>
              )}
            </button>
          </form>

          <p className="text-gray-500 text-xs text-center mt-4">
            Your information is safe and will never be shared.
          </p>
        </div>

        {/* Powered by Footer */}
        <div className="text-center mt-6">
          <a
            href="https://vaulto.social"
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-500 text-sm hover:text-gray-400 transition-colors"
          >
            Powered by Vaulto
          </a>
        </div>
      </div>
    </div>
  );
}
